import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/App';

// Counter animation hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
    }
  }, [startOnView]);

  useEffect(() => {
    if (startOnView && ref.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasStarted) {
            setHasStarted(true);
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(ref.current);
      return () => observer.disconnect();
    }
  }, [startOnView, hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration, hasStarted]);

  return { count, ref };
};

// Format number with Turkish locale
const formatNumber = (num) => {
  return num.toLocaleString('tr-TR');
};

const Hero = () => {
  const { language } = useLanguage();

  const content = {
    tr: {
      titleLine1: 'Anne Influencer Pazarlamasında',
      titleLine2: 'Türkiye\'nin Uzman ve Veri Odaklı Ajansı',
      subtitleLine1: 'Anne-Bebek, Anne-Çocuk ve Ev Kadını influencer\'larla markanızı büyütüyoruz.',
      subtitleLine2: 'Influencer seçiminden kampanya yönetimine kadar',
      subtitleLine3: 'her adımı veri odaklı, şeffaf ve profesyonelce yürütüyoruz.',
      stats: [
        { number: 25, suffix: '+', label: 'KURUMSAL MARKA' },
        { number: 250, suffix: '+', label: 'INFLUENCER İLE ÇALIŞTIK' },
        { number: 1000, suffix: '+', label: 'İÇERİK ÜRETİMİ' }
      ]
    },
    en: {
      titleLine1: 'In Mom Influencer Marketing',
      titleLine2: 'Turkey\'s Expert and Data-Driven Agency',
      subtitleLine1: 'We grow your brand with Mom-Baby, Mom-Child and Housewife influencers.',
      subtitleLine2: 'From influencer selection to campaign management,',
      subtitleLine3: 'we handle every step in a data-driven, transparent and professional manner.',
      stats: [
        { number: 25, suffix: '+', label: 'CORPORATE BRANDS' },
        { number: 250, suffix: '+', label: 'INFLUENCERS WORKED WITH' },
        { number: 1000, suffix: '+', label: 'CONTENT PRODUCED' }
      ]
    }
  };

  const t = content[language];

  // Counter hooks for each stat
  const counter1 = useCountUp(t.stats[0].number, 2000);
  const counter2 = useCountUp(t.stats[1].number, 2000);
  const counter3 = useCountUp(t.stats[2].number, 2000);
  const counters = [counter1, counter2, counter3];

  return (
    <section id="hero" className="pt-20 pb-8 flex items-center bg-black bg-opacity-70 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#FFDD59] rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl animate-float"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10 w-full">
        {/* Vertical Layout: Title -> Subtitle -> Stats */}
        <div className="flex flex-col items-center text-center mt-8">
          {/* Main Title */}
          <div className="animate-slide-in-left w-full">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight text-white">
              {t.titleLine1}<br/>
              {t.titleLine2}
            </h1>
          </div>

          {/* Subtitle */}
          <div className="mt-4 max-w-3xl">
            <p className="text-base md:text-lg text-gray-300 leading-relaxed">
              {t.subtitleLine1}<br/>
              {t.subtitleLine2}<br/>
              {t.subtitleLine3}
            </p>
          </div>

          {/* Stats Boxes - Horizontal on desktop, vertical on mobile */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5 w-full max-w-3xl">
            {t.stats.map((stat, index) => (
              <div
                key={index}
                ref={counters[index].ref}
                className="relative bg-[#141414] rounded-[14px] px-5 py-6 text-center overflow-hidden"
                style={{
                  border: '0.5px solid rgba(245, 197, 24, 0.15)'
                }}
              >
                {/* Gold gradient line at top */}
                <div 
                  className="absolute top-0 left-0 right-0 h-[2px]"
                  style={{
                    background: 'linear-gradient(90deg, transparent, #F5C518, transparent)'
                  }}
                ></div>
                
                {/* Number */}
                <div className="text-3xl md:text-[40px] font-extrabold text-[#F5C518] leading-none">
                  {formatNumber(counters[index].count)}{stat.suffix}
                </div>
                
                {/* Label */}
                <div 
                  className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[1.5px]"
                  style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
